/**
 * @typedef {Object} DependencyFallbackConfig
 *
 * @property {string} name - The name of the fallback dependency.
 * @property {RegExp[]} patterns - An array of regular expressions for resources related to the given dependency.
 * @property {number} fallbackDelay - The delay in milliseconds to wait before loading the fallback dependency.
 */

/**
 * @typedef {Object} DeferredDependencyLoaderOptions
 * @property {boolean} [debug] - Whether to enable debug mode or not.
 * @property {number} [threshold] - The intersection threshold for when to load dependencies.
 * @property {string} [rootMargin] - The root margin for the IntersectionObserver.
 * @property {DependencyFallbackConfig[]} [fallbacks] - An array of fallback configurations for when dependencies should load in.
 */

class DeferredDependencyLoader {
  /**
   * An object mapping service names to their respective regular expressions for resource identification.
   *
   * @type {Object.<string, RegExp[]>}
   * @property {RegExp[]} [key] - An array of regular expressions for resources related to the given key or service.
   */

  dependencies = {
    nosto: [/nosto.com/],
    smile: [/smile-loader.js/, /js.smile.io/],
    stamped: [/stamped.io/],
    swym: [/swymrelay.com/, /swymv3free-01.azureedge.net/],
    reputon: [/reputon.com/],
    tolstoy: [/gotolstoy.com/],
  };

  /**
   * @type {string[]}
   */
  loadedDependencies = [];

  /**
   * A map of dependency fallback configurations, grouped by delay time.
   * @type {Map<number, DependencyFallbackConfig[]>}
   */
  fallbacks = new Map();

  debug = false;
  threshold = 0.25;
  rootMargin = '0px 0px 0px 0px';

  /**
   * @type {number[]}
   */
  fallbackDelays = [];

  /**
   *
   * @param {DeferredDependencyLoaderOptions} options
   */
  constructor(options) {
    this.loadedDependencies = [];

    if (options) {
      if (typeof options?.debug === 'boolean') this.debug = options.debug;
      if (typeof options?.threshold === 'number')
        this.threshold = options.threshold;
      if (typeof options?.rootMargin === 'string')
        this.rootMargin = options.rootMargin;
      if (Array.isArray(options?.fallbacks)) {
        /**
         * Group fallbacks by delay time
         * @type {Map<number, DependencyFallbackConfig[]>}
         */
        let initialValue = new Map();
        this.fallbacks = options.fallbacks.reduce((acc, fallback) => {
          this.fallbackDelays.push(fallback.fallbackDelay);

          if (!acc.get(fallback.fallbackDelay))
            acc.set(fallback.fallbackDelay, []);

          acc.get(fallback.fallbackDelay).push(fallback);

          return acc;
        }, initialValue);
      }
    }

    // Start with all dependencies blocked
    Object.values(this.dependencies).forEach((dependency) => {
      window.YETT_BLACKLIST.push(...dependency);
    });

    let elemsWithDependencies = document.querySelectorAll('[data-depends-on]');

    if (elemsWithDependencies.length) {
      elemsWithDependencies.forEach((elem) => {
        let dependencies = elem.dataset.dependsOn;

        if (!dependencies) return;

        try {
          dependencies = dependencies.replace(/\s+/g, '').split(',');
        } catch (e) {
          console.error("Couldn't parse dependencies for element", elem, e);
          return;
        }

        // Set up intersection observer to wait for this element to be visible
        let observer = new IntersectionObserver((entries) => {
          entries.forEach(
            (entry) => {
              if (
                entry.isIntersecting &&
                window.dependencyLoader.needsLoading(dependencies)
              ) {
                if (window.dependencyLoader.debug)
                  console.log(
                    'Intersection observed. Loading dependencies and disconnecting observer bound to',
                    entry.target,
                  );
                observer.disconnect();
                window.dependencyLoader.loadDependencies(dependencies);
              }
            },
            {
              threshold: this.threshold,
              rootMargin: this.rootMargin,
            },
          );
        });

        observer.observe(elem);
      });
    }

    // Start timeouts for fallback groups
    for (let i = 0; i < this.fallbackDelays.length; i++) {
      setTimeout(() => {
        let groups = this.fallbacks.get(this.fallbackDelays[i]);

        if (!groups.length) return;

        for (let j = 0; j < groups.length; j++) {
          if (this.debug) console.debug(`Loading fallback: ${groups[j].name}`);

          window.yett.unblock(...groups[j].patterns);
        }
      }, this.fallbackDelays[i]);
    }
  }

  /**
   * Allows the selected dependencies to be loaded
   * @param {string[]} [deps]
   * @returns {boolean} true if all dependencies are loaded
   */
  loadDependencies(deps) {
    if (typeof deps === 'undefined') {
      console.debug('No dependencies provided, unblocking all');
      this.loadedDependencies = Object.keys(this.dependencies);
      return window.yett.unblock();
    }

    if (typeof deps === 'string') deps = [deps];

    return deps
      .filter(
        (dependency) => this.loadedDependencies.indexOf(dependency) === -1,
      )
      .forEach((dependency) => {
        try {
          this.loadedDependencies.push(dependency);
          window.yett.unblock(...this.dependencies[dependency]);

          return true;
        } catch (e) {
          console.error(
            'Failed to load dependency',
            dependency,
            'with error:',
            e,
          );
        }
      });
  }

  /**
   * Checks if any of the given dependencies are not loaded
   * @param {string[]} deps Dependency names to check for loaded status
   * @returns {boolean} true if any of the dependencies are not loaded
   */
  needsLoading(deps) {
    if (typeof deps === 'undefined') return false;

    if (typeof deps === 'string') deps = [deps];

    return deps.some((dep) => this.loadedDependencies.indexOf(dep) === -1);
  }
}

window.DeferredDependencyLoader = DeferredDependencyLoader;
